<script setup lang="ts">
import type { AllSteps, CompanyDetails, Step } from "../types";

const props = defineProps<{
  steps: AllSteps;
  currentStep: Step<any> | null;
  companyDetails: CompanyDetails | null;
  futureStepsDisabled: boolean;
  clientRequestComplete: boolean;
}>();

const emit = defineEmits<(e: "step:select", step: Step<any>) => void>();

function isStepDisabled(step: Step<any>) {
  // If the step is completed and not editable, it is disabled
  if (!!step.completed_at && !step.is_editable) {
    return true;
  }

  // If the step is not completed and is a future step and the future steps are disabled, it is disabled
  if (
    !step.completed_at &&
    props.futureStepsDisabled &&
    step.value !== props.currentStep?.value
  ) {
    return true;
  }

  // If the client request is complete, all steps are disabled
  if (props.clientRequestComplete) {
    return true;
  }

  return false;
}
</script>

<template>
    <button
        v-for="step in steps"
        @click="emit('step:select', step)"
        data-testid="step"
        :id="`step-selector-${step.value}`"
        type="button"
        :aria-disabled="isStepDisabled(step)"
        :disabled="isStepDisabled(step)"
        aria-haspopup="listbox"
        class="step-selector-value"
        :class="{
            'light-css': companyDetails?.light_css,
        }"
        >
        <div class="step-selector-value-label">
            <lds-icon
            :class="{ completed: step.completed_at, 'light-css': companyDetails?.light_css }"
            :name="
                step.completed_at
                ? 'lds-icon-CheckCircleFilled'
                : step.value === currentStep?.value
                ? 'lds-icon-RadioButtonOn'
                : 'lds-icon-RadioButtonOff'
            "
            ></lds-icon>
            {{ step.label }}
        </div>
    </button>
</template>

<style scoped lang="scss">
.step-selector-value {
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: $spacing-xs 0;
  border: none;
  background-color: transparent;
  color: $color-neutral-900;
  @include typography-body-bold;
  &[disabled] {
    color: $color-neutral-500;
    cursor: not-allowed;
    pointer-events: none;
  }
  &.light-css {
    color: $color-neutral-0;
  }
}

.step-selector-value-label {
  display: flex;
  align-items: center;

  lds-icon {
    margin-right: $spacing-xs;

    &.completed {
      color: $color-primary-500;
    }
    &.light-css {
      color: $color-neutral-0;
    }
  }
}
</style>